import React, { useState, Fragment } from 'react';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Select } from '../components/ui/Select';
import { Badge } from '../components/ui/Badge';
import { ReportPreviewModal } from '../components/fulfillment/ReportPreviewModal';
import { CloseoutConfirmationModal } from '../components/fulfillment/CloseoutConfirmationModal';
import { mockStores } from '../utils/mockData';
import { calculateStoreStatus } from '../utils/storeStatus';
import { Download, CheckCircle, Circle, Clock } from 'lucide-react';
import { useToast } from '../hooks/useToast';
type ReportType = 'production-summary' | 'decoration-report' | 'personalization-roster' | 'shipping-addresses';
type ProductionStatus = 'closed' | 'in-production' | 'packed' | 'shipped' | 'completed';
interface ChecklistItem {
  id: string;
  label: string;
  completed: boolean;
}
export function Fulfillment() {
  const {
    showToast
  } = useToast();
  // Store selection state
  const [selectedStoreId, setSelectedStoreId] = useState<string>('');
  const [loadedStore, setLoadedStore] = useState<(typeof mockStores)[0] | null>(null);
  const [productionStatus, setProductionStatus] = useState<ProductionStatus>('closed');
  // Checklist state
  const [checklist, setChecklist] = useState<ChecklistItem[]>([{
    id: '1',
    label: 'Verify all order totals match payment gateway',
    completed: false
  }, {
    id: '2',
    label: 'Review flagged orders for personalization issues',
    completed: false
  }, {
    id: '3',
    label: 'Confirm production inventory availability',
    completed: false
  }, {
    id: '4',
    label: 'Generate final production reports',
    completed: false
  }]);
  // Modal state
  const [reportPreview, setReportPreview] = useState<{
    isOpen: boolean;
    type: ReportType | null;
  }>({
    isOpen: false,
    type: null
  });
  const [confirmationModal, setConfirmationModal] = useState<{
    isOpen: boolean;
    type: 'mark-production' | 'mark-completed' | null;
  }>({
    isOpen: false,
    type: null
  });
  // Get closed stores only
  const closedStores = mockStores.filter(store => {
    const status = calculateStoreStatus(store);
    return status === 'closed';
  });
  const handleLoadStore = () => {
    if (!selectedStoreId) {
      showToast('Please select a closed store.', 'error');
      return;
    }
    const store = closedStores.find(s => s.id === selectedStoreId);
    if (store) {
      setLoadedStore(store);
      setProductionStatus('closed');
      showToast(`Loaded ${store.name}`, 'success');
    }
  };
  const toggleChecklistItem = (id: string) => {
    setChecklist(prev => prev.map(item => item.id === id ? {
      ...item,
      completed: !item.completed
    } : item));
  };
  const completedCount = checklist.filter(item => item.completed).length;
  const totalCount = checklist.length;
  const canMarkProduction = completedCount >= 3;
  const getReportData = (type: ReportType) => {
    const reports = {
      'production-summary': {
        title: 'Production Summary',
        description: 'Grouped totals by product, color, and size for production planning.',
        columns: ['Product Name', 'Color', 'Size', 'Quantity', 'Unit Price', 'Total'],
        data: [['Performance T-Shirt', 'Navy', 'Adult L', '45', '$29.99', '$1,349.55'], ['Performance T-Shirt', 'Navy', 'Adult M', '38', '$29.99', '$1,139.62'], ['Performance T-Shirt', 'Navy', 'Youth L', '22', '$24.99', '$549.78'], ['Team Hoodie', 'Gray', 'Adult L', '34', '$54.99', '$1,869.66'], ['Team Hoodie', 'Gray', 'Adult M', '28', '$54.99', '$1,539.72'], ['Team Cap', 'Navy', 'One Size', '45', '$19.99', '$899.55'], ['Team Cap', 'Gray', 'One Size', '22', '$19.99', '$439.78'], ['Gear Bag', 'Navy', 'Standard', '18', '$39.99', '$719.82'], ['Player Pack Bundle', 'Mixed', 'Adult L', '15', '$94.99', '$1,424.85'], ['Player Pack Bundle', 'Mixed', 'Youth L', '8', '$79.99', '$639.92']]
      },
      'decoration-report': {
        title: 'Decoration Report',
        description: 'Decoration details grouped by design and location for production workflow.',
        columns: ['Design Name', 'Location', 'Print Method', 'Product', 'Color', 'Size', 'Quantity'],
        data: [['Team Logo V2', 'Front', 'Screen Print', 'Performance T-Shirt', 'Navy', 'Adult L', '45'], ['Team Logo V2', 'Back', 'Screen Print', 'Performance T-Shirt', 'Navy', 'Adult L', '45'], ['Team Logo V2', 'Front', 'Screen Print', 'Performance T-Shirt', 'Navy', 'Adult M', '38'], ['Team Logo Embroidery', 'Left Chest', 'Embroidery', 'Team Hoodie', 'Gray', 'Adult L', '34'], ['Team Logo Embroidery', 'Left Chest', 'Embroidery', 'Team Hoodie', 'Gray', 'Adult M', '28'], ['Team Logo Small', 'Front', 'Embroidery', 'Team Cap', 'Navy', 'One Size', '45'], ['Team Logo Small', 'Front', 'Embroidery', 'Team Cap', 'Gray', 'One Size', '22'], ['Team Logo V2', 'Side Panel', 'Screen Print', 'Gear Bag', 'Navy', 'Standard', '18'], ['Bundle Multi', 'Multiple', 'Mixed', 'Player Pack Bundle', 'Mixed', 'Adult L', '15'], ['Bundle Multi', 'Multiple', 'Mixed', 'Player Pack Bundle', 'Mixed', 'Youth L', '8']]
      },
      'personalization-roster': {
        title: 'Personalization Roster',
        description: 'Complete roster of all personalized items with names and numbers.',
        columns: ['Order #', 'Customer Name', 'Product', 'Size', 'Color', 'Name', 'Number', 'Qty'],
        data: [['ORD123', 'John Doe', 'Performance T-Shirt', 'Adult L', 'Navy', 'SMITH', '23', '2'], ['ORD124', 'Jane Smith', 'Performance T-Shirt', 'Adult M', 'Navy', 'JOHNSON', '15', '1'], ['ORD125', 'Bob Wilson', 'Performance T-Shirt', 'Youth L', 'Navy', 'WILSON', '7', '1'], ['ORD126', 'Alice Brown', 'Performance T-Shirt', 'Adult L', 'Navy', 'BROWN', '42', '1'], ['ORD127', 'Charlie Davis', 'Performance T-Shirt', 'Adult M', 'Navy', 'DAVIS', '88', '2'], ['ORD128', 'Emma Wilson', 'Player Pack Bundle', 'Adult L', 'Mixed', 'E. WILSON', '11', '1'], ['ORD129', 'Liam Johnson', 'Player Pack Bundle', 'Youth L', 'Mixed', 'JOHNSON', '5', '1'], ['ORD130', 'Olivia Martinez', 'Performance T-Shirt', 'Adult L', 'Navy', 'MARTINEZ', '33', '1'], ['ORD131', 'Noah Anderson', 'Performance T-Shirt', 'Adult M', 'Navy', 'ANDERSON', '9', '2'], ['ORD132', 'Ava Taylor', 'Performance T-Shirt', 'Youth L', 'Navy', 'TAYLOR', '14', '1']]
      },
      'shipping-addresses': {
        title: 'Shipping Addresses',
        description: 'Complete shipping address list for all ship-to-home orders.',
        columns: ['Order #', 'Customer', 'Email', 'Phone', 'Address 1', 'City', 'State', 'ZIP', 'Method'],
        data: [['ORD123', 'John Doe', 'john@example.com', '(555) 123-4567', '123 Main St', 'Springfield', 'IL', '62704', 'Standard'], ['ORD124', 'Jane Smith', 'jane@example.com', '(555) 234-5678', '456 Oak Ave', 'Springfield', 'IL', '62701', 'Standard'], ['ORD125', 'Bob Wilson', 'bob@example.com', '(555) 345-6789', '789 Elm St', 'Springfield', 'IL', '62702', 'Express'], ['ORD126', 'Alice Brown', 'alice@example.com', '(555) 456-7890', '321 Pine Rd', 'Springfield', 'IL', '62703', 'Standard'], ['ORD127', 'Charlie Davis', 'charlie@example.com', '(555) 567-8901', '654 Maple Dr', 'Springfield', 'IL', '62705', 'Standard'], ['ORD128', 'Emma Wilson', 'emma@example.com', '(555) 678-9012', '987 Cedar Ln', 'Springfield', 'IL', '62706', 'Express'], ['ORD129', 'Liam Johnson', 'liam@example.com', '(555) 789-0123', '246 Birch St', 'Springfield', 'IL', '62707', 'Standard'], ['ORD130', 'Olivia Martinez', 'olivia@example.com', '(555) 890-1234', '135 Spruce Ave', 'Springfield', 'IL', '62708', 'Standard'], ['ORD131', 'Noah Anderson', 'noah@example.com', '(555) 901-2345', '864 Willow Rd', 'Springfield', 'IL', '62709', 'Express'], ['ORD132', 'Ava Taylor', 'ava@example.com', '(555) 012-3456', '753 Ash Dr', 'Springfield', 'IL', '62710', 'Standard']]
      }
    };
    return reports[type];
  };
  const handleOpenReport = (type: ReportType) => {
    setReportPreview({
      isOpen: true,
      type
    });
  };
  const handleDownloadReport = () => {
    if (!reportPreview.type) return;
    const report = getReportData(reportPreview.type);
    showToast(`${report.title} downloaded`, 'success');
    setReportPreview({
      isOpen: false,
      type: null
    });
  };
  const handleMarkProduction = () => {
    setConfirmationModal({
      isOpen: true,
      type: 'mark-production'
    });
  };
  const handleMarkCompleted = () => {
    setConfirmationModal({
      isOpen: true,
      type: 'mark-completed'
    });
  };
  const handleConfirmAction = () => {
    if (confirmationModal.type === 'mark-production') {
      setProductionStatus('in-production');
      showToast(`${loadedStore?.name} marked as In Production`, 'success');
    } else if (confirmationModal.type === 'mark-completed') {
      setProductionStatus('completed');
      showToast(`${loadedStore?.name} marked as Completed`, 'success');
    }
    setConfirmationModal({
      isOpen: false,
      type: null
    });
  };
  const getStatusBadgeVariant = (status: ProductionStatus) => {
    switch (status) {
      case 'closed':
        return 'red';
      case 'in-production':
        return 'yellow';
      case 'packed':
        return 'blue';
      case 'shipped':
        return 'blue';
      case 'completed':
        return 'green';
      default:
        return 'gray';
    }
  };
  const getStatusLabel = (status: ProductionStatus) => {
    return status.split('-').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
  };
  const productionTimeline = [{
    label: 'Received',
    status: 'closed',
    completed: true
  }, {
    label: 'In Production',
    status: 'in-production',
    completed: productionStatus !== 'closed'
  }, {
    label: 'Packed',
    status: 'packed',
    completed: ['packed', 'shipped', 'completed'].includes(productionStatus)
  }, {
    label: 'Shipped',
    status: 'shipped',
    completed: ['shipped', 'completed'].includes(productionStatus)
  }, {
    label: 'Completed',
    status: 'completed',
    completed: productionStatus === 'completed'
  }];
  const currentReport = reportPreview.type ? getReportData(reportPreview.type) : null;
  return <div className="space-y-6" data-id="element-655">
      <div data-id="element-656">
        <h1 className="text-2xl font-bold text-gray-900" data-id="element-657">
          Fulfillment & Closeout
        </h1>
        <p className="text-gray-500" data-id="element-658">
          Manage production, exports, and store closeout processes.
        </p>
      </div>

      {/* Store Selector */}
      <Card title="Select Store" data-id="element-659">
        <div className="space-y-4" data-id="element-660">
          <Select label="Select a closed store..." value={selectedStoreId} onChange={e => setSelectedStoreId(e.target.value)} options={[{
          value: '',
          label: 'Choose a store...'
        }, ...closedStores.map(s => ({
          value: s.id,
          label: `${s.name} (${s.stats.ordersCount} orders, $${s.stats.revenue.toFixed(2)})`
        }))]} data-id="element-661" />
          <Button onClick={handleLoadStore} disabled={!selectedStoreId} data-id="element-662">
            Load Store
          </Button>
        </div>
      </Card>

      {/* Loaded Store Content */}
      {loadedStore && <>
          {/* Store Summary */}
          <Card title="Store Summary" data-id="element-663">
            <div className="space-y-4" data-id="element-664">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-665">
                <div data-id="element-666">
                  <p className="text-sm text-gray-600" data-id="element-667">Store Name</p>
                  <p className="font-medium text-gray-900" data-id="element-668">
                    {loadedStore.name}
                  </p>
                </div>
                <div data-id="element-669">
                  <p className="text-sm text-gray-600" data-id="element-670">Close Date/Time</p>
                  <p className="font-medium text-gray-900" data-id="element-671">
                    {new Date(loadedStore.settings.closeDate).toLocaleString()}
                  </p>
                </div>
                <div data-id="element-672">
                  <p className="text-sm text-gray-600" data-id="element-673">Total Orders</p>
                  <p className="text-2xl font-bold text-gray-900" data-id="element-674">
                    {loadedStore.stats.ordersCount}
                  </p>
                </div>
                <div data-id="element-675">
                  <p className="text-sm text-gray-600" data-id="element-676">Total Revenue</p>
                  <p className="text-2xl font-bold text-gray-900" data-id="element-677">
                    ${loadedStore.stats.revenue.toFixed(2)}
                  </p>
                </div>
                <div data-id="element-678">
                  <p className="text-sm text-gray-600" data-id="element-679">Fulfillment Mode</p>
                  <p className="font-medium text-gray-900" data-id="element-680">
                    {loadedStore.settings.orderingEnabled ? 'Ship-to-home' : 'Bulk Ship'}
                  </p>
                </div>
                <div data-id="element-681">
                  <p className="text-sm text-gray-600" data-id="element-682">Current Status</p>
                  <Badge variant={getStatusBadgeVariant(productionStatus)} data-id="element-683">
                    {getStatusLabel(productionStatus)}
                  </Badge>
                </div>
              </div>
            </div>
          </Card>

          {/* Production Timeline (shown after marking in production) */}
          {productionStatus !== 'closed' && <Card title="Production Timeline" data-id="element-684">
              <div className="flex items-center justify-between" data-id="element-685">
                {productionTimeline.map((step, idx) => <Fragment key={step.status} data-id="element-686">
                    <div className="flex flex-col items-center" data-id="element-687">
                      <div className={`
                        w-10 h-10 rounded-full flex items-center justify-center mb-2
                        ${step.completed ? 'bg-green-500 text-white' : 'bg-gray-200 text-gray-400'}
                      `} data-id="element-688">
                        {step.completed ? <CheckCircle className="h-6 w-6" data-id="element-689" /> : <Circle className="h-6 w-6" data-id="element-690" />}
                      </div>
                      <span className={`text-xs font-medium ${step.completed ? 'text-gray-900' : 'text-gray-500'}`} data-id="element-691">
                        {step.label}
                      </span>
                    </div>
                    {idx < productionTimeline.length - 1 && <div className={`flex-1 h-1 mx-2 ${step.completed ? 'bg-green-500' : 'bg-gray-200'}`} data-id="element-692" />}
                  </Fragment>)}
              </div>
            </Card>}

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-id="element-693">
            {/* Left Column - Exports & Checklist */}
            <div className="lg:col-span-2 space-y-6" data-id="element-694">
              {/* Export Reports */}
              <Card title="Export Reports" data-id="element-695">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-id="element-696">
                  <Button variant="outline" className="justify-start" onClick={() => handleOpenReport('production-summary')} data-id="element-697">
                    <Download className="h-4 w-4 mr-2" data-id="element-698" />
                    Production Summary
                  </Button>
                  <Button variant="outline" className="justify-start" onClick={() => handleOpenReport('decoration-report')} data-id="element-699">
                    <Download className="h-4 w-4 mr-2" data-id="element-700" />
                    Decoration Report
                  </Button>
                  <Button variant="outline" className="justify-start" onClick={() => handleOpenReport('personalization-roster')} data-id="element-701">
                    <Download className="h-4 w-4 mr-2" data-id="element-702" />
                    Personalization Roster
                  </Button>
                  <Button variant="outline" className="justify-start" onClick={() => handleOpenReport('shipping-addresses')} data-id="element-703">
                    <Download className="h-4 w-4 mr-2" data-id="element-704" />
                    Shipping Addresses
                  </Button>
                </div>
              </Card>

              {/* Closeout Checklist */}
              <Card title="Closeout Checklist" description={`${completedCount}/${totalCount} complete`} data-id="element-705">
                <div className="space-y-3" data-id="element-706">
                  {checklist.map(item => <label key={item.id} className="flex items-start space-x-3 p-3 rounded-lg hover:bg-gray-50 cursor-pointer transition-colors" data-id="element-707">
                      <input type="checkbox" checked={item.completed} onChange={() => toggleChecklistItem(item.id)} className="h-5 w-5 text-red-600 focus:ring-red-500 border-gray-300 rounded mt-0.5" data-id="element-708" />
                      <span className={`text-sm flex-1 ${item.completed ? 'text-gray-500 line-through' : 'text-gray-900'}`} data-id="element-709">
                        {item.label}
                      </span>
                    </label>)}
                </div>

                {/* Progress Bar */}
                <div className="mt-4 pt-4 border-t border-gray-200" data-id="element-710">
                  <div className="flex items-center justify-between text-sm mb-2" data-id="element-711">
                    <span className="text-gray-600" data-id="element-712">Progress</span>
                    <span className="font-medium text-gray-900" data-id="element-713">
                      {Math.round(completedCount / totalCount * 100)}%
                    </span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2" data-id="element-714">
                    <div className="bg-red-600 h-2 rounded-full transition-all duration-300" style={{
                  width: `${completedCount / totalCount * 100}%`
                }} data-id="element-715" />
                  </div>
                </div>
              </Card>
            </div>

            {/* Right Column - Actions */}
            <div className="space-y-6" data-id="element-716">
              <Card title="Store Actions" data-id="element-717">
                <div className="space-y-3" data-id="element-718">
                  {productionStatus === 'closed' && <>
                      <Button className="w-full" onClick={handleMarkProduction} disabled={!canMarkProduction} data-id="element-719">
                        Mark Store In Production
                      </Button>
                      {!canMarkProduction && <p className="text-xs text-gray-500 italic" data-id="element-720">
                          Complete at least 3 checklist items to mark in
                          production
                        </p>}
                    </>}

                  {productionStatus === 'in-production' && <Button className="w-full" onClick={handleMarkCompleted} data-id="element-721">
                      Mark Store Completed
                    </Button>}

                  {productionStatus === 'completed' && <div className="flex items-center justify-center p-4 bg-green-50 border border-green-200 rounded-lg" data-id="element-722">
                      <CheckCircle className="h-5 w-5 text-green-600 mr-2" data-id="element-723" />
                      <span className="text-sm font-medium text-green-900" data-id="element-724">
                        Store Completed
                      </span>
                    </div>}
                </div>
              </Card>

              <Card title="Quick Stats" data-id="element-725">
                <div className="space-y-3 text-sm" data-id="element-726">
                  <div className="flex justify-between" data-id="element-727">
                    <span className="text-gray-600" data-id="element-728">Total Items</span>
                    <span className="font-medium text-gray-900" data-id="element-729">
                      {loadedStore.stats.ordersCount * 2}
                    </span>
                  </div>
                  <div className="flex justify-between" data-id="element-730">
                    <span className="text-gray-600" data-id="element-731">Avg Order Value</span>
                    <span className="font-medium text-gray-900" data-id="element-732">
                      $
                      {(loadedStore.stats.revenue / loadedStore.stats.ordersCount).toFixed(2)}
                    </span>
                  </div>
                  <div className="flex justify-between" data-id="element-733">
                    <span className="text-gray-600" data-id="element-734">Days Since Close</span>
                    <span className="font-medium text-gray-900" data-id="element-735">
                      {Math.floor((Date.now() - new Date(loadedStore.settings.closeDate).getTime()) / (1000 * 60 * 60 * 24))}
                    </span>
                  </div>
                </div>
              </Card>
            </div>
          </div>
        </>}

      {/* Report Preview Modal */}
      {currentReport && <ReportPreviewModal isOpen={reportPreview.isOpen} onClose={() => setReportPreview({
      isOpen: false,
      type: null
    })} reportTitle={currentReport.title} columns={currentReport.columns} sampleData={currentReport.data} onDownload={handleDownloadReport} data-id="element-736" />}

      {/* Confirmation Modals */}
      {confirmationModal.type === 'mark-production' && <CloseoutConfirmationModal isOpen={confirmationModal.isOpen} onClose={() => setConfirmationModal({
      isOpen: false,
      type: null
    })} action="mark-production" storeName={loadedStore?.name || ''} onConfirm={handleConfirmAction} data-id="element-737" />}

      {confirmationModal.type === 'mark-completed' && <CloseoutConfirmationModal isOpen={confirmationModal.isOpen} onClose={() => setConfirmationModal({
      isOpen: false,
      type: null
    })} action="reopen" storeName={loadedStore?.name || ''} onConfirm={handleConfirmAction} data-id="element-738" />}
    </div>;
}